import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, ButtonGroup, Choose, Dialog, Input, Label, Select, SelectTrigger } from "@activecollab/components";
import { Field, Form, Formik } from "formik";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";
import { useIntercomContext } from "~/components/Providers/Intercom/context";
import SetPasswordDialog from "~/pages/Onboarding/SetPasswordDialog";
import { useIndustryNames } from "~/repository/useIndustries";
import { SUBMIT_ORGANIZATION_DETAILS_ONBOARDING_SUCCESS } from "~/store/action_types";
import { submitOrganizationDetailsOnboarding } from "~/store/actions/actions.users";
import { shouldShowGettingStarted } from "~/store/selectors/selector.configOptions";
import { getFeatureFlag } from "~/store/selectors/selector.featureFlags";

export const OrgDetailsOnboarding = () => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { setIsIntercomStarted } = useIntercomContext();
  const navigate = useNavigate();
  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();
  const hasFF = useSelector((state) => getFeatureFlag(state, "getting_started"));
  const showGettingStarted = useSelector(shouldShowGettingStarted);
  const { data: fetchedIndustries } = useIndustryNames();
  const industries = useMemo(() => {
    return fetchedIndustries?.map((industry) => {
      return { id: industry, name: industry };
    });
  }, [fetchedIndustries]);
  const sizes = useMemo(() => {
    return [
      { id: 1, name: "Only me", choose: "Only me", min_size: 1 },
      { id: 2, name: "2-10 employees", choose: "2-10", min_size: 2 },
      { id: 3, name: "11-50 employees", choose: "11-50", min_size: 11 },
      { id: 4, name: "51-200 employees", choose: "51-200", min_size: 51 },
      { id: 5, name: "201-500 employees", choose: "201-500", min_size: 201 },
      { id: 6, name: "501-1000 employees", choose: "501-1000", min_size: 501 },
      { id: 7, name: "1001+ employees", choose: "1001+", min_size: 1001 },
    ];
  }, []);
  const { t } = useTranslation();
  const resolveSizeLabel = useCallback(
    (id) => {
      if (!id) {
        return `${t("Select size")}...`;
      }
      return sizes.find((i) => i?.id === id)?.name;
    },
    [sizes, t]
  );

  const handleSubmit = useCallback(
    (values) => {
      const data = { ...values };
      data.industry = values.industry;
      data.size = sizes.find((i) => i.id === values.size)?.name;
      data.min_size = sizes.find((i) => i.id === values.size)?.min_size;
      setIsLoading(true);
      dispatch(submitOrganizationDetailsOnboarding(data)).then((result) => {
        setIsLoading(false);
        if (result?.type === SUBMIT_ORGANIZATION_DETAILS_ONBOARDING_SUCCESS) {
          setIsIntercomStarted(true);
          navigate(hasFF && showGettingStarted ? "/getting-started" : "/projects");
        }
      });
    },
    [dispatch, hasFF, navigate, setIsIntercomStarted, showGettingStarted, sizes]
  );

  return (
    <Formik
      initialValues={{ company_name: "", industry: null, size: null }}
      validate={(values) => {
        const errors = {};
        if (!values?.company_name) {
          errors.company_name = "Must enter Company Name";
        }

        if (!values?.industry) {
          errors.industry = "Must enter Industry";
        }

        if (!values?.size) {
          errors.size = "Must enter Size";
        }

        return errors;
      }}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ isValid, submitForm, errors, touched }) => {
        return (
          <>
            <SetPasswordDialog
              isValid={isValid}
              title={t("Organization Information")}
            >
              <Form className="tw-flex tw-flex-col tw-gap-6">
                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-grow-0 tw-gap-6">
                  <Field name="company_name">
                    {({ field }) => (
                      <div className="tw-flex tw-flex-col tw-w-full sm:tw-w-64">
                        <Label
                          htmlFor="company_name"
                          className="tw-mb-1"
                          variant="Caption 1"
                          invalid={touched.company_name && errors.company_name}
                        >
                          {t("Organization Name")}*
                        </Label>
                        <Input
                          id="company_name"
                          data-testid="company_name"
                          className="tw-w-full tw-shrink-0"
                          value={field.value}
                          onChange={field.onChange}
                          maxLength={100}
                          placeholder="ACME Inc."
                          invalid={touched.company_name && errors.company_name}
                          ref={(el) => (fieldRef.current.company_name = el)}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="industry">
                    {({ field, form }) => (
                      <div className="tw-flex tw-flex-col tw-w-full sm:tw-w-64 tw-shrink-0">
                        <Label
                          htmlFor="industry"
                          className="tw-mb-1"
                          variant="Caption 1"
                          invalid={touched.industry && errors.industry}
                        >
                          {t("Industry")}*
                        </Label>
                        <Select
                          target={
                            <SelectTrigger
                              className="tw-w-full"
                              invalid={touched.industry && errors.industry}
                              ref={(el) => (fieldRef.current.industry = el)}
                            >
                              {field.value ? field.value : `${t("Select industry")}...`}
                            </SelectTrigger>
                          }
                          options={industries}
                          onChange={(v) => form.setFieldValue(field.name, v)}
                          selected={field.value}
                          forceCloseMenu
                          disabledInternalSort
                          keepSameOptionsOrder
                        ></Select>
                      </div>
                    )}
                  </Field>
                </div>
                <Field name="size">
                  {({ field, form }) => {
                    return (
                      <div className="tw-flex tw-flex-col tw-w-full">
                        <Label
                          htmlFor="size"
                          className="tw-mb-1"
                          variant="Caption 1"
                          invalid={touched.size && errors.size}
                        >
                          {t("Organization Size")}*
                        </Label>
                        <ButtonGroup
                          id="size"
                          className="tw-hidden sm:tw-flex"
                          invalid={touched.size && errors.size}
                          data-testid="button-group"
                        >
                          {sizes.map((option) => {
                            return (
                              <Choose
                                data-testid={`test-option-${option.id}`}
                                key={option.id}
                                type="button"
                                className="tw-p-2 button-group-choose tw-h-[42px] md:tw-h-[32px]"
                                active={field.value === option.id}
                                onClick={() => form.setFieldValue(field.name, option.id)}
                              >
                                {option.choose}
                              </Choose>
                            );
                          })}
                        </ButtonGroup>
                        <Select
                          target={
                            <SelectTrigger
                              invalid={touched.size && errors.size}
                              className="tw-w-full tw-flex sm:tw-hidden"
                              data-testid="select-trigger"
                            >
                              {resolveSizeLabel(field.value)}
                            </SelectTrigger>
                          }
                          options={sizes}
                          onChange={(v) => form.setFieldValue(field.name, v)}
                          selected={field.value}
                          forceCloseMenu
                          disabledInternalSort
                          keepSameOptionsOrder
                          invalid={touched.size && errors.size}
                        ></Select>
                      </div>
                    );
                  }}
                </Field>
              </Form>
            </SetPasswordDialog>
            <Dialog.Actions>
              <Button
                onClick={() => {
                  focusFirstInvalidField(errors, false);
                  submitForm();
                }}
                disabled={isLoading}
              >
                {t(`Continue to Your Account`)}
              </Button>
            </Dialog.Actions>
          </>
        );
      }}
    </Formik>
  );
};
